import { apiRequest } from '@/utils/api-request';
import { useOrgStore } from '@/stores/org-store';

interface CollectionSearchProps {
  collectionId: string;
  query: string;
  dateRange?: {
    from: Date;
    to: Date;
  };
  scoreThreshold?: number;
  expandQuery?: boolean;
  enableRecencyBoost?: boolean;
  topK?: number;
  customFilters?: Array<{ key: string; value: string }>;
}

export interface CollectionSearchResult {
  id: number;
  version: number;
  score: number;
  payload: {
    title?: string;
    datetime: string;
    text: string;
    chunk_start_index: number;
    external_id: string;
    chunk_end_index: number;
    chunk_token_count: number;
    chunk_level: number;
    index_time: string;
    metadata?: Record<string, unknown>;
  };
  vector: unknown;
  shard_key: unknown;
  order_value: unknown;
}

interface CollectionSearchResponse {
  results: CollectionSearchResult[];
  count: number;
  detail?: string;
}

export const searchCollection = async ({
  collectionId,
  query,
  dateRange,
  scoreThreshold,
  expandQuery,
  enableRecencyBoost,
  topK,
  customFilters,
}: CollectionSearchProps): Promise<CollectionSearchResponse> => {
  const orgId = useOrgStore.getState().currentOrg?.orgid;

  const body: Record<string, unknown> = {
    query,
  };

  if (dateRange) {
    body.start_date = dateRange.from.toISOString().split('T')[0];
    body.end_date = dateRange.to.toISOString().split('T')[0];
  }

  if (scoreThreshold !== undefined) {
    body.score_threshold = scoreThreshold;
  }

  if (expandQuery !== undefined) {
    body.expand_query = expandQuery;
  }

  if (enableRecencyBoost !== undefined) {
    body.enable_recency_boost = enableRecencyBoost;
  }

  if (topK !== undefined) {
    body.top_k = topK;
  }

  if (customFilters && customFilters.length > 0) {
    body.custom_filters = customFilters;
  }

  const response = await apiRequest<CollectionSearchResponse>({
    url: `/collections/${orgId}/${collectionId}/search/hybrid`,
    method: 'POST',
    body,
    target: 'platform',
  });

  if (response?.detail) {
    return {
      results: [],
      count: 0,
      detail: response.detail || 'Failed to search collection',
    };
  }

  return response.data as CollectionSearchResponse;
};
